import { Link, Outlet, useLocation } from "react-router-dom";
import { AlertTriangle, Settings as SettingsIcon } from "lucide-react";
import { useServerBase } from "../lib/settings";
import { useServerVersionCheck } from "../lib/version";

// Safari enforces mixed-content blocking for http://localhost and
// http://127.0.0.1 from HTTPS pages — unlike Chromium-family browsers
// which treat loopback as a potentially-trustworthy exception. Nothing
// we can do from JS; just detect and show a clear notice.
function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Chrome on iOS also includes "Safari" but reports CriOS/FxiOS/etc.
  return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS/.test(ua);
}

export function Layout() {
  const [serverBase] = useServerBase();
  const loc = useLocation();
  const versionState = useServerVersionCheck();
  // Block all routes except Settings when the installed MobAI is outdated.
  const blocked = versionState.outdated === true && loc.pathname !== "/settings";
  const safari = isSafari();
  if (safari) return <SafariBlock />;
  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-surface-800 bg-surface-900/80 backdrop-blur sticky top-0 z-20">
        <div className="mx-auto max-w-5xl px-4 h-12 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-surface-100 font-medium">
            <img src="/appicon.svg" alt="MobAI" className="w-6 h-6 rounded-md" />
            <span>MobAI</span>
            <span className="ml-2 text-xs font-normal text-surface-600 hidden sm:inline">
              {serverBase}
            </span>
          </Link>
          <Link
            to="/settings"
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${
              loc.pathname === "/settings"
                ? "bg-surface-800 text-accent-cyan"
                : "text-surface-300 hover:bg-surface-800"
            }`}
          >
            <SettingsIcon className="w-3.5 h-3.5" />
            Settings
          </Link>
        </div>
      </header>
      <main className="flex-1 min-h-0">{blocked ? <OutdatedBlock /> : <Outlet />}</main>
    </div>
  );
}

function SafariBlock() {
  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-surface-800 bg-surface-900/80 backdrop-blur sticky top-0 z-20">
        <div className="mx-auto max-w-5xl px-4 h-12 flex items-center">
          <div className="flex items-center gap-2 text-surface-100 font-medium">
            <img src="/appicon.svg" alt="MobAI" className="w-6 h-6 rounded-md" />
            <span>MobAI</span>
          </div>
        </div>
      </header>
      <main className="flex-1 min-h-0">
        <div className="mx-auto max-w-lg px-4 py-16 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-300" />
          </div>
          <h1 className="text-lg font-semibold mb-2">Safari isn't supported</h1>
          <p className="text-sm text-surface-400 mb-4">
            Safari blocks HTTPS pages from connecting to{" "}
            <code className="text-surface-300">http://127.0.0.1</code>, which is how
            this page talks to your local MobAI server. Chrome, Edge, Arc, and other
            Chromium-based browsers allow it.
          </p>
          <p className="text-sm text-surface-400">
            Open{" "}
            <code className="text-surface-200">app.mobai.run</code> in Chrome, or
            use the MobAI desktop app directly.
          </p>
        </div>
      </main>
    </div>
  );
}

function OutdatedBlock() {
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 mb-4">
        <AlertTriangle className="w-6 h-6 text-amber-300" />
      </div>
      <h1 className="text-lg font-semibold mb-2">Update MobAI to continue</h1>
      <p className="text-sm text-surface-400">
        Your MobAI is out of date. In the MobAI app open{" "}
        <span className="text-surface-200">Support</span> →{" "}
        <span className="text-surface-200">About MobAI</span> →{" "}
        <span className="text-surface-200">Check For Updates</span>.
      </p>
    </div>
  );
}
